import type { ZudokuConfig } from "zudoku";
import { getDevdocsNavItems } from "../shared/config/navigation";
import urls, { getPlatformUrl } from "../shared/config/urls";
import {
  getBrowserDocument,
  getBrowserWindow,
} from "../shared/lib/runtime";
import CustomFooter from "./components/CustomFooter";
import { LogoUrlUpdater } from "./components/LogoUrlUpdater";
import { ChangelogPage } from "./components/ChangelogPage";
import { PageBanner } from "./components/PageBanner";
import ZudokuMobileNavigation from "./components/ZudokuMobileNavigation";
import { ZudokuNavigation } from "./components/ZudokuNavigation";
import redirects from "./redirects.json";
import { remarkEnvironmentLinks } from "./plugins/remarkEnvironmentLinks";

// TypeScript declarations for Google Analytics
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    NREUM?: unknown;
    __nrAgentInitialized?: boolean;
    __yvAppKeyObserver?: MutationObserver;
  }
}

const APP_KEY_STORAGE_KEY = "yv-platform:app-key";
const APP_KEY_INPUT_ID = "yvp-app-key-input";
const APP_KEY_IDENTITY_ID = "youversion-app-key";
const APP_KEY_HEADER_NAME = "x-yvp-app-key";

const getNewRelicDebugState = () => ({
  environment: process.env.ZUDOKU_PUBLIC_ENVIRONMENT,
  hasAppId: Boolean(process.env.ZUDOKU_PUBLIC_NEW_RELIC_APP_ID),
  hasLicenseKey: Boolean(process.env.ZUDOKU_PUBLIC_NEW_RELIC_LICENSE_KEY),
  hasAccountId: Boolean(process.env.ZUDOKU_PUBLIC_NEW_RELIC_ACCOUNT_ID),
  hasAgentId: Boolean(process.env.ZUDOKU_PUBLIC_NEW_RELIC_AGENT_ID),
  hasTrustKey: Boolean(process.env.ZUDOKU_PUBLIC_NEW_RELIC_TRUST_KEY),
  hadExistingNreum: Boolean(getBrowserWindow()?.NREUM),
  alreadyInitialized: Boolean(getBrowserWindow()?.__nrAgentInitialized),
});

const getNewRelicOptions = () => {
  const appId = process.env.ZUDOKU_PUBLIC_NEW_RELIC_APP_ID;
  const licenseKey = process.env.ZUDOKU_PUBLIC_NEW_RELIC_LICENSE_KEY;
  const accountId = process.env.ZUDOKU_PUBLIC_NEW_RELIC_ACCOUNT_ID;
  const agentId = process.env.ZUDOKU_PUBLIC_NEW_RELIC_AGENT_ID;
  const trustKey = process.env.ZUDOKU_PUBLIC_NEW_RELIC_TRUST_KEY;

  if (!appId || !licenseKey || !accountId || !agentId || !trustKey) {
    return null;
  }

  return {
    info: {
      applicationID: appId,
      beacon: "bam.nr-data.net",
      errorBeacon: "bam.nr-data.net",
      licenseKey,
      sa: 1,
    },
    init: {
      ajax: {
        deny_list: ["bam.nr-data.net"],
      },
      browser_consent_mode: {
        enabled: false,
      },
      distributed_tracing: {
        enabled: true,
      },
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true,
      },
      privacy: {
        cookies_enabled: true,
      },
    },
    loader_config: {
      accountID: accountId,
      agentID: agentId,
      applicationID: appId,
      licenseKey,
      trustKey,
    },
  };
};

const initializeNewRelic = () => {
  const isPreprod = process.env.ZUDOKU_PUBLIC_ENVIRONMENT === "preprod";
  const canInitializeNewRelic =
    getBrowserWindow() !== null && getBrowserDocument() !== null;

  if (!canInitializeNewRelic) {
    return;
  }

  if (window.__nrAgentInitialized) {
    if (isPreprod) {
      console.info("[NR] Browser Agent skipped (already initialized)", {
        ...getNewRelicDebugState(),
        initialized: false,
        skipped: true,
      });
    }
    return;
  }

  const newRelicOptions = getNewRelicOptions();
  if (!newRelicOptions) {
    if (isPreprod) {
      console.info("[NR] Browser Agent skipped (missing config)", {
        ...getNewRelicDebugState(),
        initialized: false,
        skipped: true,
      });
    }
    return;
  }

  void import("@newrelic/browser-agent/loaders/browser-agent")
    .then(({ BrowserAgent }) => {
      if (window.__nrAgentInitialized) {
        if (isPreprod) {
          console.info("[NR] Browser Agent skipped (already initialized)", {
            ...getNewRelicDebugState(),
            initialized: false,
            skipped: true,
          });
        }
        return;
      }

      new BrowserAgent(newRelicOptions);
      window.__nrAgentInitialized = true;
      if (isPreprod) {
        console.info("[NR] Browser Agent initialized", {
          ...getNewRelicDebugState(),
          initialized: true,
          hasNreumAfterInit: Boolean(window.NREUM),
        });
      }
    })
    .catch((error) => {
      if (isPreprod) {
        console.warn("[NR] Browser Agent failed to initialize", {
          ...getNewRelicDebugState(),
          initialized: false,
          error,
        });
      }
    });
};

const selectAppKeyIdentity = () => {
  const doc = getBrowserDocument();
  if (!doc) return;
  const identityControl = doc.getElementById(APP_KEY_IDENTITY_ID);
  if (!identityControl) return;

  const isAlreadySelected =
    identityControl.getAttribute("data-state") === "checked";
  if (!isAlreadySelected) {
    identityControl.dispatchEvent(
      new MouseEvent("click", { bubbles: true, cancelable: true })
    );
  }
};

const readStoredAppKey = (): string | undefined => {
  const win = getBrowserWindow();
  if (!win) return undefined;
  try {
    return (
      win.sessionStorage?.getItem(APP_KEY_STORAGE_KEY) ??
      win.localStorage?.getItem(APP_KEY_STORAGE_KEY) ??
      undefined
    );
  } catch {
    return undefined;
  }
};

const persistAppKey = (value?: string) => {
  const win = getBrowserWindow();
  if (!win) return;
  try {
    if (value) {
      win.sessionStorage?.setItem(APP_KEY_STORAGE_KEY, value);
      win.localStorage?.setItem(APP_KEY_STORAGE_KEY, value);
    } else {
      win.sessionStorage?.removeItem(APP_KEY_STORAGE_KEY);
      win.localStorage?.removeItem(APP_KEY_STORAGE_KEY);
    }
  } catch {
    // Best-effort persistence only.
  }
};

const mountAppKeyField = () => {
  const win = getBrowserWindow();
  const doc = getBrowserDocument();
  if (!win || !doc) {
    return;
  }

  const ensureField = () => {
    const identityControl = doc.getElementById(APP_KEY_IDENTITY_ID);
    if (!identityControl) return;

    selectAppKeyIdentity();

    const radioGroup = identityControl.closest<HTMLElement>(
      '[role="radiogroup"]'
    );
    if (!radioGroup) return;

    const host = radioGroup.parentElement;
    if (!host || host.querySelector("[data-yvp-app-key-field]")) return;

    radioGroup.style.display = "none";
    radioGroup.setAttribute("aria-hidden", "true");

    const wrapper = doc.createElement("div");
    wrapper.dataset.yvpAppKeyField = "true";
    wrapper.className =
      "flex flex-col gap-2 border-b border-border p-4 bg-background";

    const label = doc.createElement("label");
    label.className =
      "text-xs font-semibold tracking-tight text-muted-foreground";
    label.htmlFor = APP_KEY_INPUT_ID;
    label.innerHTML =
      'YVP App Key <span style="color: var(--destructive);">*</span>';

    const input = doc.createElement("input");
    input.id = APP_KEY_INPUT_ID;
    input.type = "text";
    input.autocomplete = "off";
    input.placeholder = "Paste your App Key";
    input.required = true;
    input.className =
      "w-full rounded-md border border-border px-3 py-2 text-sm font-mono bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    input.value = readStoredAppKey() ?? "";
    input.addEventListener("input", (event) => {
      if (!(event.currentTarget instanceof HTMLInputElement)) return;
      persistAppKey(event.currentTarget.value.trim() || undefined);
    });

    wrapper.append(label, input);
    host.append(wrapper);
  };

  ensureField();

  if (win.__yvAppKeyObserver) {
    return;
  }

  if (!("MutationObserver" in win) || !("MutationObserver" in globalThis)) {
    return;
  }
  const observer = new globalThis.MutationObserver(() => ensureField());
  observer.observe(doc.body, { childList: true, subtree: true });
  win.__yvAppKeyObserver = observer;
};

const config: ZudokuConfig = {
  // SAFETY: Zudoku's theme type omits Tweakcn tokens (shadow-*, sidebar*, font*); the runtime CSS still consumes them.
  theme: {
    defaultThemeMode: "system",
    customCss: `
      /* Hide the theme switcher button in header */
      header button.size-9 {
        display: none !important;
      }
      /* Alternative selectors in case the button structure is different */
      header [aria-label*="theme" i],
      header [aria-label*="toggle" i]:has(svg),
      header button:has(svg[class*="sun"]),
      header button:has(svg[class*="moon"]),
      header button[data-theme-toggle] {
        display: none !important;
      }

      /* Ensure dark mode is properly applied */
      html.dark,
      html[data-theme="dark"] {
        color-scheme: dark;
      }

      html.light,
      html[data-theme="light"] {
        color-scheme: light;
      }

      /* Ensure theme classes are respected - Zudoku uses CSS variables */
      html.dark {
        /* Dark mode is active - ensure variables are used */
      }

      html.light {
        /* Light mode is active - ensure variables are used */
      }
    `,
    light: {
      background: "0 10.0000% 96.0784%",
      foreground: "0 2.9412% 13.3333%",
      card: "0 0% 100%",
      cardForeground: "0 2.9412% 13.3333%",
      popover: "0 0% 100%",
      popoverForeground: "0 0% 7.0588%",
      primary: "7.2115 88.8889% 54.1176%",
      primaryForeground: "0 10.0000% 96.0784%",
      secondary: "0 5.2632% 92.5490%",
      secondaryForeground: "0 3.8251% 35.8824%",
      muted: "0 5.2632% 92.5490%",
      mutedForeground: "50.0000 2.3622% 50.1961%",
      accent: "0 5.2632% 92.5490%",
      accentForeground: "49.0909 7.6923% 28.0392%",
      destructive: "60 2.5641% 7.6471%",
      destructiveForeground: "0 0% 100%",
      border: "50.0000 18.1818% 87.0588%",
      input: "0 0% 100%",
      ring: "32.0000 15.4639% 80.9804%",
      "chart-1": "7.2115 88.8889% 54.1176%",
      "chart-2": "33.1579 87.1560% 57.2549%",
      "chart-3": "50.7692 7.9755% 68.0392%",
      "chart-4": "357.8571 52.5000% 31.3725%",
      "chart-5": "33.5821 79.7619% 32.9412%",
      sidebar: "0 5.2632% 92.5490%",
      sidebarForeground: "60 2.5210% 23.3333%",
      sidebarPrimary: "7.2115 88.8889% 54.1176%",
      sidebarPrimaryForeground: "0 0% 98.4314%",
      sidebarAccent: "0 0% 84.3137%",
      sidebarAccentForeground: "0 0% 20.3922%",
      sidebarBorder: "0 0% 92.1569%",
      sidebarRing: "0 0% 70.9804%",
      fontSans: "Inter, ui-sans-serif, sans-serif, system-ui",
      fontSerif: "Merriweather, ui-serif, serif",
      fontMono: "Space Mono, ui-monospace, monospace",
      radius: "0.5rem",
      "shadow-x": "0",
      "shadow-y": "1px",
      "shadow-blur": "3px",
      "shadow-spread": "0px",
      "shadow-opacity": "0.1",
      "shadow-color": "oklch(0 0 0)",
      "shadow-2xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
      "shadow-xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
      "shadow-sm":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
      shadow:
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
      "shadow-md":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)",
      "shadow-lg":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)",
      "shadow-xl":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)",
      "shadow-2xl": "0 1px 3px 0px hsl(0 0% 0% / 0.25)",
      "tracking-normal": "0em",
      spacing: "0.25rem",
    },
    dark: {
      background: "0 0% 7.0588%",
      foreground: "46.1538 9.7744% 73.9216%",
      card: "0 2.9412% 13.3333%",
      cardForeground: "48 33.3333% 97.0588%",
      popover: "60 2.1277% 18.4314%",
      popoverForeground: "60 5.4545% 89.2157%",
      primary: "7.2115 88.8889% 54.1176%",
      primaryForeground: "0 10.0000% 96.0784%",
      secondary: "60 2.1277% 18.4314%",
      secondaryForeground: "0 10.0000% 96.0784%",
      muted: "60 3.8462% 10.1961%",
      mutedForeground: "51.4286 8.8608% 69.0196%",
      accent: "0 0% 7.0588%",
      accentForeground: "0 10.0000% 96.0784%",
      destructive: "0 84.2365% 60.1961%",
      destructiveForeground: "0 0% 100%",
      border: "60 1.9608% 20%",
      input: "48.0000 2.7933% 35.0980%",
      ring: "0 3.4483% 17.0588%",
      "chart-1": "7.2115 88.8889% 54.1176%",
      "chart-2": "33.1579 87.1560% 57.2549%",
      "chart-3": "48 10.6383% 9.2157%",
      "chart-4": "357.8571 52.5000% 31.3725%",
      "chart-5": "33.5821 79.7619% 32.9412%",
      sidebar: "0 2.9412% 13.3333%",
      sidebarForeground: "46.1538 9.7744% 73.9216%",
      sidebarPrimary: "0 0% 20.3922%",
      sidebarPrimaryForeground: "0 0% 98.4314%",
      sidebarAccent: "0 0% 20.3922%",
      sidebarAccentForeground: "46.1538 9.7744% 73.9216%",
      sidebarBorder: "0 0% 92.1569%",
      sidebarRing: "0 0% 70.9804%",
      fontSans: "Inter, ui-sans-serif, sans-serif, system-ui",
      fontSerif: "Merriweather, ui-serif, serif",
      fontMono: "Space Mono, ui-monospace, monospace",
      radius: "0.5rem",
      "shadow-x": "0",
      "shadow-y": "1px",
      "shadow-blur": "3px",
      "shadow-spread": "0px",
      "shadow-opacity": "0.1",
      "shadow-color": "oklch(0 0 0)",
      "shadow-2xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
      "shadow-xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
      "shadow-sm":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
      shadow:
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
      "shadow-md":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)",
      "shadow-lg":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)",
      "shadow-xl":
        "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)",
      "shadow-2xl": "0 1px 3px 0px hsl(0 0% 0% / 0.25)",
    },
  } as ZudokuConfig["theme"],
  metadata: {
    title: "%s | YouVersion Platform Developer Docs",
    defaultTitle: "YouVersion Platform Developer Docs",
    description: "API and SDK docs for the YouVersion Platform",
    favicon: "/favicon.ico?v=3",
    keywords: [
      "youversion platform",
      "youversion",
      "christian developers",
      "bible licensing",
    ],
    creator: "YouVersion",
    publisher: "YouVersion Platform",
  },
  canonicalUrlOrigin: urls.devdocs.production,
  enableStatusPages: true,
  // Generates `sitemap.xml` in the build output so search engines can
  // discover all documentation pages. Redirect sources are excluded
  // explicitly because Zudoku <=0.69 does not yet filter them automatically
  // (see https://github.com/zuplo/zudoku/issues/2075).
  sitemap: {
    siteUrl: urls.devdocs.production,
    changefreq: "weekly",
    priority: 0.7,
    autoLastmod: true,
    exclude: [...redirects.map(({ from }) => from), "/api/~schemas"],
  },
  site: {
    title: "YouVersion Platform Developer Docs",
    showPoweredBy: false,
    logo: {
      src: {
        light: "/yvPlatform-logo-light.svg",
        dark: "/yvPlatform-logo-dark.svg",
      },
      alt: "YouVersion Platform",
      width: "237px",
      href: getPlatformUrl(),
    },
    // banner: {
    //   message: "Welcome to our documentation!",
    //   color: "info",
    //   dismissible: true
    // },
    footer: {
      //   //position: "center",
      //   copyright: ` `,
      //   // Other options...
    },
  },
  build: {
    remarkPlugins: [remarkEnvironmentLinks],
  },
  // Add MDX components configuration
  mdx: {
    components: {
      ChangelogPage: ChangelogPage,
      PageBanner: PageBanner,
    },
  },
  // Documentation configuration
  docs: {
    files: "/pages/**/*.{md,mdx}",
    publishMarkdown: true,
    llms: { llmsTxt: true, llmsTxtFull: true },
    defaultOptions: {
      toc: true,
      disablePager: false,
      showLastModified: true,
      copyPage: true,
    },
  },
  // Add slots configuration to integrate your navigation
  slots: {
    // "layout-before-head": () => (
    //   <div className="flex items-center border border-gray-200 pb-2">
    //     <img
    //       src="/youversion-logo.png"
    //       alt="YouVersion Bible API"
    //       className="h-8 w-auto"
    //     />
    //     <span className="ml-2 font-semibold text-lg">YouVersion Platform</span>
    //   </div>
    // ),
    "head-navigation-end": () => <LogoUrlUpdater />,
    // this SLOT only fires on desktop
    "head-navigation-start": () => (
      <ZudokuNavigation navItems={getDevdocsNavItems()} />
    ),
    // this SLOT only fires on mobile
    "top-navigation-side": () => (
      <ZudokuMobileNavigation navItems={getDevdocsNavItems()} />
    ),

    "footer-before": () => <CustomFooter />,
  },

  plugins: [
    {
      initialize: () => {
        const win = getBrowserWindow();
        if (!win) return;
        try {
          const STORAGE_KEY = "identity-storage"; // zudoku remember key
          const NO_IDENTITY = "__none"; // zudoku's internal none id
          const DESIRED_ID = "youversion-app-key";

          const raw = win.sessionStorage.getItem(STORAGE_KEY);
          const parsed = raw ? JSON.parse(raw) : undefined;
          const current = parsed?.state?.rememberedIdentity;

          // Only set if not chosen yet or explicitly set to none
          if (!current || current === NO_IDENTITY) {
            const next = {
              ...parsed,
              state: {
                ...parsed?.state,
                rememberedIdentity: DESIRED_ID,
              },
              version: parsed?.version ?? 0,
            };
            win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          }
        } catch {
          // ignore
        }
      },
    },
    {
      // Theme sync plugin - syncs with main platform's theme system
      initialize: () => {
        const win = getBrowserWindow();
        const doc = getBrowserDocument();
        if (!win || !doc) {
          return;
        }

        const THEME_STORAGE_KEY = "youversion-theme"; // Same as main platform
        type Theme = "dark" | "light" | "system";

        const root = doc.documentElement;

        const applyTheme = (theme: Theme) => {
          // Remove existing theme classes
          root.classList.remove("light", "dark");

          let effectiveTheme: "light" | "dark";

          if (theme === "system") {
            // Detect system preference
            const prefersDark = win.matchMedia(
              "(prefers-color-scheme: dark)"
            ).matches;
            effectiveTheme = prefersDark ? "dark" : "light";
          } else {
            effectiveTheme = theme;
          }

          // Apply the theme class to HTML element
          root.classList.add(effectiveTheme);

          // Also set data-theme attribute for Zudoku compatibility
          root.setAttribute("data-theme", effectiveTheme);
        };

        // Get theme from localStorage (syncs with main platform)
        const getStoredTheme = (): Theme => {
          try {
            const stored = localStorage.getItem(THEME_STORAGE_KEY);
            if (
              stored === "dark" ||
              stored === "light" ||
              stored === "system"
            ) {
              return stored;
            }
          } catch {
            // Ignore localStorage errors
          }
          return "system"; // Default to system
        };

        // Apply theme immediately on initialization
        const initialTheme = getStoredTheme();
        applyTheme(initialTheme);

        // Listen for system preference changes when theme is "system"
        let mediaQuery: MediaQueryList | null = null;
        let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null =
          null;

        const setupSystemListener = () => {
          if (mediaQuery) {
            // Clean up existing listener
            if (mediaQueryListener) {
              if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", mediaQueryListener);
              } else {
                mediaQuery.removeListener(mediaQueryListener);
              }
            }
          }

          mediaQuery = win.matchMedia("(prefers-color-scheme: dark)");
          mediaQueryListener = () => {
            const currentTheme = getStoredTheme();
            if (currentTheme === "system") {
              applyTheme("system");
            }
          };

          if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", mediaQueryListener);
          } else {
            // Fallback for older browsers
            mediaQuery.addListener(mediaQueryListener);
          }
        };

        // Setup system listener if theme is system
        if (initialTheme === "system") {
          setupSystemListener();
        }

        // Listen for storage changes (when theme is changed in main platform)
        const handleStorageChange = (e: StorageEvent) => {
          if (e.key === THEME_STORAGE_KEY && e.newValue) {
            const newTheme = e.newValue;
            if (
              newTheme !== "dark" &&
              newTheme !== "light" &&
              newTheme !== "system"
            ) {
              return;
            }
            applyTheme(newTheme);

            // Update system listener if needed
            if (newTheme === "system") {
              setupSystemListener();
            } else if (mediaQuery && mediaQueryListener) {
              // Remove listener if not using system theme
              if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", mediaQueryListener);
              } else {
                mediaQuery.removeListener(mediaQueryListener);
              }
            }
          }
        };

        win.addEventListener("storage", handleStorageChange);

        // Also listen for custom events (for same-tab updates)
        const handleThemeChange = (event: Event) => {
          if (!(event instanceof CustomEvent)) return;
          const theme = event.detail?.theme;
          if (theme !== "dark" && theme !== "light" && theme !== "system") {
            return;
          }
          applyTheme(theme);
          if (theme === "system") {
            setupSystemListener();
          }
        };

        win.addEventListener("theme-change", handleThemeChange);

        // Poll for localStorage changes (for same-tab sync).
        // Intentionally not captured for cleanup: this plugin has no teardown
        // hook, so the interval runs for the lifetime of the page, which is fine.
        let lastTheme = initialTheme;
        setInterval(() => {
          const currentTheme = getStoredTheme();
          if (currentTheme !== lastTheme) {
            lastTheme = currentTheme;
            applyTheme(currentTheme);
            if (currentTheme === "system") {
              setupSystemListener();
            }
          }
        }, 100);
      },
    },
    {
      initialize: () => {
        mountAppKeyField();

        initializeNewRelic();

        // Ensure Google Analytics is loaded (fallback if getHead doesn't work)
        const win = getBrowserWindow();
        const doc = getBrowserDocument();
        if (win && doc && !win.gtag) {
          // Initialize dataLayer and gtag function
          win.dataLayer = win.dataLayer || [];
          win.gtag = function () {
            // Mirrors Google's canonical gtag snippet verbatim (see the inline
            // <script> version below); GA expects the raw `arguments` object.
            win.dataLayer.push(arguments);
          };
          win.gtag("js", new Date());
          win.gtag("config", "G-W12SJPTYLX");

          // Load the gtag.js script
          const script = doc.createElement("script");
          script.async = true;
          script.src =
            "https://www.googletagmanager.com/gtag/js?id=G-W12SJPTYLX";
          doc.head.appendChild(script);
        }
      },
    },
    {
      initialize: () => {
        const win = getBrowserWindow();
        const doc = getBrowserDocument();
        if (!win || !doc) return;
        doc.addEventListener("click", (e) => {
          if (!(e.target instanceof Element)) return;
          const anchor = e.target.closest("a");
          if (!anchor) return;

          const href = anchor.getAttribute("href") || "";
          const text = anchor.textContent?.trim() || "";
          const section = anchor.closest("nav")
            ? "sidebar_nav"
            : anchor.closest("header")
              ? "header"
              : anchor.closest("footer")
                ? "footer"
                : "content";

          if (win.gtag) {
            win.gtag("event", "docs_click", {
              link_text: text.slice(0, 100),
              link_url: href,
              section,
              page_path: win.location.pathname,
            });
          }
        });
      },
    },
    {
      getHead: () => (
        <>
          {/* Theme sync script - must run before page render to prevent flash */}
          <script
            dangerouslySetInnerHTML={{
              __html: `
                (function() {
                  try {
                    const THEME_STORAGE_KEY = 'youversion-theme';
                    const root = document.documentElement;

                    // Get theme from localStorage (syncs with main platform)
                    let theme = 'system';
                    try {
                      const stored = localStorage.getItem(THEME_STORAGE_KEY);
                      if (stored === 'dark' || stored === 'light' || stored === 'system') {
                        theme = stored;
                      }
                    } catch (e) {
                      // Ignore localStorage errors
                    }

                    // Determine effective theme
                    let effectiveTheme = theme;
                    if (theme === 'system') {
                      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                      effectiveTheme = prefersDark ? 'dark' : 'light';
                    }

                    // Apply theme class immediately (before page render)
                    root.classList.remove('light', 'dark');
                    root.classList.add(effectiveTheme);
                    root.setAttribute('data-theme', effectiveTheme);
                  } catch (e) {
                    // Fallback: ensure at least light mode is applied
                    document.documentElement.classList.add('light');
                  }
                })();
              `,
            }}
          />
          <link rel="icon" href="/favicon.ico?v=3" sizes="any" />
          <link
            rel="shortcut icon"
            href="/favicon.ico?v=3"
            type="image/x-icon"
          />
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
          />
          {/* Mobile responsive overrides - see MOBILE_STYLES.md */}
          <link rel="stylesheet" href="/mobile-overrides.css" />
          {/* Google tag (gtag.js) - inline script must come first */}
          {/* Security note: This script is from Google Analytics (trusted source) and is intentionally
              injected for tracking. The script content is hardcoded and not from user input. */}
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-W12SJPTYLX');
              `,
            }}
          />
          <script
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-W12SJPTYLX"
          ></script>
        </>
      ),
    },
    {
      events: {
        location: ({ from, to }) => {
          if (!getBrowserWindow()) return;

          // Function to send page view
          const sendPageView = () => {
            if (window.gtag) {
              window.gtag("config", "G-W12SJPTYLX", {
                page_path: to.pathname,
                page_title: document.title,
                page_location: window.location.href,
              });
              return true;
            }
            // Fallback: use dataLayer with correct gtag format
            if (window.dataLayer) {
              window.dataLayer.push([
                "config",
                "G-W12SJPTYLX",
                {
                  page_path: to.pathname,
                  page_title: document.title,
                  page_location: window.location.href,
                },
              ]);
              return true;
            }
            return false;
          };

          // Try to send immediately
          if (sendPageView()) {
            return;
          }

          // If gtag/dataLayer not ready, wait a bit and retry (for initial load)
          if (!from) {
            // Initial page load - retry after a short delay
            setTimeout(() => {
              if (!sendPageView()) {
                // Last resort: wait a bit more for async script to load
                setTimeout(sendPageView, 500);
              }
            }, 100);
          } else {
            // Subsequent navigation - should have gtag by now, but retry once
            setTimeout(sendPageView, 100);
          }
        },
      },
    },
    {
      getIdentities: async () => [
        {
          id: APP_KEY_IDENTITY_ID,
          label: "YVP App Key",
          default: true,
          authorizeRequest: (request) => {
            if (getBrowserWindow()) {
              const storedKey = readStoredAppKey();
              if (storedKey) {
                request.headers.set(APP_KEY_HEADER_NAME, storedKey);
              } else {
                // Throw an error to prevent the request from being sent
                throw new Error(
                  "YVP App Key is required. Please enter your App Key before sending the request."
                );
              }
            }
            return request;
          },
        },
      ],
    },
  ],

  navigation: [
    {
      type: "category",
      label: "Overview",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: false,
          icon: "sparkles",
          items: ["/overview", "/api-usage"],
        },
        {
          type: "category",
          label: "Guides",
          collapsible: false,
          icon: "book",
          items: [
            "/guides/display-bible-html",
            "/guides/language-ranges",
            "/sign-in-apis",
            "/search-apis",
            "/guides/images",
            "/usfm-reference",
            "/error-codes",
          ],
        },
        {
          type: "category",
          label: "Useful Links",
          collapsible: false,
          icon: "link",
          items: [
            {
              type: "link",
              icon: "globe",
              label: "YouVersion",
              to: "https://youversion.com/",
            },
            {
              type: "link",
              icon: "github",
              label: "GitHub",
              to: "https://github.com/youversion",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      icon: "code",
      label: "API Reference",
      to: "/api",
    },

    {
      type: "category",
      label: "SDKs",
      icon: "book",
      items: [
        "/sdks/introduction",
        {
          type: "category",
          label: "Swift SDK",
          collapsible: true,
          collapsed: true,
          items: [
            "/sdks/swift/quick-start",
            "/sdks/swift/components",
            {
              type: "category",
              label: "Guides",
              collapsible: true,
              collapsed: true,
              items: [
                "/sdks/swift/guides/search",
                "/sdks/swift/guides/reader-navigation",
                "/sdks/swift/guides/copyright-and-attribution",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Kotlin SDK",
          collapsible: true,
          collapsed: true,
          items: [
            "/sdks/kotlin/quick-start",
            "/sdks/kotlin/components",
            {
              type: "category",
              label: "Guides",
              collapsible: true,
              collapsed: true,
              items: [
                "/sdks/kotlin/guides/search",
                "/sdks/kotlin/guides/copyright-and-attribution",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "JavaScript SDK",
          collapsible: true,
          collapsed: true,
          items: [
            "/sdks/javascript/quick-start",
            "/sdks/javascript/index",
            "/sdks/typescript-types", // The content for this page is automatically generated from the platform-sdk-react source code. (see scripts/fetch-type.mjs)
            {
              type: "category",
              label: "Guides",
              collapsible: true,
              collapsed: true,
              items: [
                "/sdks/javascript/guides/copyright-and-attribution",
                "/sdks/javascript/guides/search",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "React SDK",
          collapsible: true,
          collapsed: true,
          items: [
            "/sdks/react/quick-start",
            "/sdks/react/components",
            "/sdks/react/hooks",
            {
              type: "category",
              label: "Guides",
              collapsible: true,
              collapsed: true,
              items: [
                "/sdks/react/guides/copyright-and-attribution",
                "/sdks/react/guides/highlights",
                "/sdks/react/guides/legacy-auth-migration",
                "/sdks/react/guides/search",
                "/sdks/react/guides/text-direction",
                "/sdks/react/guides/theming",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "React Native (Expo) SDK",
          collapsible: true,
          collapsed: true,
          items: [
            "/sdks/react-native-expo/quick-start",
            "/sdks/react-native-expo/components",
            "/sdks/react-native-expo/search",
            "/sdks/react-native-expo/authentication",
            {
              type: "category",
              label: "Guides",
              collapsible: true,
              collapsed: true,
              items: [
                "/sdks/react-native-expo/guides/copyright-and-attribution",
                "/sdks/react-native-expo/guides/highlights",
                "/sdks/react-native-expo/guides/localization",
              ],
            },
          ],
        },
      ],
    },
    {
      type: "link",
      icon: "list",
      label: "Changelog",
      to: "/changelog",
    },
  ],
  redirects,
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
    },
  ],
  search: {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
};

export default config;
